import type { OrderBookingSnapshot } from './checkoutOrderTypes';
import {
  type AggregateCheckoutStatus,
  type ShopOrderStatus,
  resolveShopOrderStatus,
} from './orderStatus';

const TERMINAL_NEGATIVE: ShopOrderStatus[] = ['Cancelled', 'Rejected'];
const SHIPPED: ShopOrderStatus[] = ['Dispatched', 'InTransit', 'Delivered'];
const DELIVERED: ShopOrderStatus[] = ['Delivered'];
const REFUNDED: ShopOrderStatus[] = ['Refunded'];
const ACTIVE: ShopOrderStatus[] = [
  'Pending',
  'Accepted',
  'Packed',
  'Dispatched',
  'InTransit',
  'Delivered',
];

function statusesForBookings(bookings: OrderBookingSnapshot[]): ShopOrderStatus[] {
  return bookings.map(resolveShopOrderStatus);
}

function allMatch(statuses: ShopOrderStatus[], set: ShopOrderStatus[]): boolean {
  return statuses.length > 0 && statuses.every((s) => set.includes(s));
}

function anyMatch(statuses: ShopOrderStatus[], set: ShopOrderStatus[]): boolean {
  return statuses.some((s) => set.includes(s));
}

function noneMatch(statuses: ShopOrderStatus[], set: ShopOrderStatus[]): boolean {
  return !anyMatch(statuses, set);
}

/**
 * Compute customer-facing aggregate status from sibling shop fulfillment orders.
 */
export function aggregateCheckoutStatus(
  bookings: OrderBookingSnapshot[]
): AggregateCheckoutStatus {
  if (bookings.length === 0) return 'Processing';

  const statuses = statusesForBookings(bookings);

  if (allMatch(statuses, REFUNDED)) return 'Refunded';
  if (anyMatch(statuses, REFUNDED) && !allMatch(statuses, REFUNDED)) {
    return 'PartiallyRefunded';
  }

  if (allMatch(statuses, TERMINAL_NEGATIVE)) return 'Cancelled';
  if (anyMatch(statuses, TERMINAL_NEGATIVE) && anyMatch(statuses, ACTIVE)) {
    return 'PartiallyCancelled';
  }

  if (allMatch(statuses, DELIVERED)) return 'Completed';

  const anyDelivered = anyMatch(statuses, DELIVERED);
  const anyNotDelivered = statuses.some(
    (s) => !DELIVERED.includes(s) && !TERMINAL_NEGATIVE.includes(s) && !REFUNDED.includes(s)
  );
  if (anyDelivered && anyNotDelivered) return 'PartiallyDelivered';

  const anyShipped = anyMatch(statuses, SHIPPED);
  const anyPreShip = statuses.some(
    (s) =>
      ['Pending', 'Accepted', 'Packed'].includes(s) &&
      !TERMINAL_NEGATIVE.includes(s) &&
      !REFUNDED.includes(s)
  );
  if (anyShipped && anyPreShip) return 'PartiallyShipped';

  if (noneMatch(statuses, SHIPPED) && noneMatch(statuses, TERMINAL_NEGATIVE)) {
    return 'Processing';
  }

  return 'Processing';
}
